import React from 'react';
import { ApolloProvider, from, ApolloClient, InMemoryCache } from '@apollo/client';

import authLink from './link/authLink';
import { httpLink } from './link/httpLink';
import networkErrorLink from './link/networkErrorLink';

let globalApolloClient: any = null;

/**
 * Creates and provides the apolloContext
 * to a next.js PageTree. Use it by wrapping
 * your PageComponent via HOC pattern.
 * @param {Function|Class} PageComponent
 * @param {Object} [config]
 * @param {Boolean} [config.ssr=true]
 */
export function withApollo(PageComponent: any, { ssr = true, graphqlGateway }: any = {}) {
  const WithApollo = ({ apolloClient, apolloState, ...pageProps }: any) => {
    const client = apolloClient || initApolloClient(apolloState, { graphqlGateway });

    return (
      <ApolloProvider client={client}>
        <PageComponent {...pageProps} />
      </ApolloProvider>
    );
  };

  if (process.env.NODE_ENV !== 'production') {
    const displayName = PageComponent.displayName || PageComponent.name || 'Component';

    if (displayName === 'App') {
      //
    }

    WithApollo.displayName = `withApollo(${displayName})`;
  }

  if (ssr || PageComponent.getInitialProps) {
    WithApollo.getInitialProps = async (ctx: any) => {
      const apolloClient = (ctx.apolloClient = initApolloClient({}, { graphqlGateway }));

      let pageProps = {};
      if (PageComponent.getInitialProps) {
        pageProps = await PageComponent.getInitialProps(ctx);
      }

      if (typeof window === 'undefined') {
        if (ctx.res && ctx.res.finished) {
          return pageProps;
        }
      }

      const apolloState = apolloClient.cache.extract();

      return {
        ...pageProps,
        apolloState,
      };
    };
  }

  return WithApollo;
}

/**
 * Always creates a new apollo client on the server
 * Creates or reuses apollo client in the browser.
 * @param  {Object} initialState
 */
function initApolloClient(initialState: any = {}, { graphqlGateway }: any) {
  if (typeof window === 'undefined') {
    return createApolloClient(initialState, { graphqlGateway });
  }

  if (!globalApolloClient) {
    globalApolloClient = createApolloClient(initialState, { graphqlGateway });
  }

  return globalApolloClient;
}

/**
 * Creates and configures the ApolloClient
 * @param  {Object} [initialState={}]
 */
function createApolloClient(initialState = {}, { graphqlGateway }: any) {
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: from([networkErrorLink, authLink, httpLink(graphqlGateway)]),
    cache: new InMemoryCache().restore(initialState),
  });
}
